import type { ThunkAction } from 'redux-thunk'
import type { Action } from '@reduxjs/toolkit'
import type { RootState } from '@/redux'
import { DashboardDomainRegistryAPI } from '@/API/DashboardDomainRegistry'
import { dashboardDomainRegistryActions } from '@/redux/slice/dashboardDomainRegistrySlice'

/**
 * fetchDashboardDomainsThunk — fires `GET /v1/dashboard-domains` and
 * populates `dashboardDomainRegistrySlice`. Idempotent: safe to call
 * multiple times. Once the slice is in `'ready'` state, subsequent
 * calls short-circuit so a hard-refresh remount does not re-fetch.
 *
 * The thunk is dispatched at dashboard-creation bootstrap (see
 * `useDashboardCreationBootstrap.ts` §3.2.2.E for the call site).
 *
 * Error handling: a network/auth failure marks the slice `'error'` with the
 * thrown message. Components reading from the slice render a fallback
 * (FiltersBlock falls back to its prior behaviour for the duration of the
 * session, see §3.2.2.F).
 */
export const fetchDashboardDomainsThunk =
  (
    token: string,
  ): ThunkAction<Promise<void>, RootState, unknown, Action> =>
  async (dispatch, getState) => {
    const status = getState().dashboardDomainRegistry.status
    // Idempotency: don't re-fetch if we already have the registry. 'loading'
    // is also a no-op — prevents duplicate in-flight requests when the
    // bootstrap effect double-fires (Strict Mode).
    if (status === 'ready' || status === 'loading') return

    dispatch(dashboardDomainRegistryActions.fetchStarted())

    const result = await DashboardDomainRegistryAPI.list(token)
    if (result.data?.domains) {
      dispatch(
        dashboardDomainRegistryActions.fetchSucceeded(result.data.domains),
      )
      return
    }
    dispatch(
      dashboardDomainRegistryActions.fetchFailed(
        result.err ?? 'Failed to load dashboard domain registry',
      ),
    )
  }
